import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageModal from "UIPalette/PageModal/PageModal";
import { ReactComponent as CloseCircle } from "@icons/close-circle.svg";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { useLogReportingEventFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { ReportingEventType } from "shared/constants/ReportingEventType";
import { OutcomeIllustration } from "../OutcomeIllustration/OutcomeIllustration";
import { ResourceFilter, SupportOption } from "../../enums/supportMoments/supportMoments";

export interface NegativeOutcomeScreenProps {
  option?: SupportOption;
  resourceFilter?: ResourceFilter;
  onFinish: () => void;
}

export const NegativeOutcomeScreen = ({ option, resourceFilter, onFinish }: NegativeOutcomeScreenProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "supportMoments.negativeOutcome" });
  const navigate = useNavigate();
  const { supportLink } = useMobilePlatform();
  const { trigger: triggerLogReportingEvent } = useLogReportingEventFunctionRequest();

  useEffect(() => {
    trackEvents(EventNames.SUPPORT_MOMENTS_OUTCOME_VIEWED, { value: "negative" });
  }, []);

  const handleLearnMore = () => {
    trackEvents(EventNames.SUPPORT_MOMENTS_OUTCOME_BUTTON_CLICK, { value: t("cta.learnMore") });
    navigate(`/audio-and-video/#si=${resourceFilter?.id}`, { replace: true });
  };

  const handleSpeakToSomeone = () => {
    triggerLogReportingEvent({
      body: {
        reportingEventType: ReportingEventType.SupportMomentCompleted,
        additionalInfo: option,
      },
    });

    trackEvents(EventNames.SUPPORT_MOMENTS_OUTCOME_BUTTON_CLICK, { value: t("cta.speakToSomeone") });
    navigate(supportLink, { replace: true });
  };

  const handleFinish = () => {
    trackEvents(EventNames.SUPPORT_MOMENTS_OUTCOME_BUTTON_CLICK, { value: t("cta.finish") });
    onFinish();
  };

  return (
    <PageModal
      showMobileIcon={false}
      backTitle={t("cta.finish")}
      backIcon={CloseCircle}
      onClose={handleFinish}
      secondaryCta={resourceFilter ? { title: t("cta.learnMore"), onClick: handleLearnMore } : undefined}
      primaryCta={{ title: t("cta.speakToSomeone"), onClick: handleSpeakToSomeone }}
      primaryCtaVariant={resourceFilter ? "secondary" : "default"}
      textCta={{ title: t("cta.finish"), onClick: handleFinish }}
    >
      <div className="flex flex-col items-center text-center gap-8">
        <OutcomeIllustration testId="negative-outcome-illustration" cloudCoverage="medium" centerElement="cloud" />
        <div className="space-y-2">
          <h1>{t("heading")}</h1>
          <p>{resourceFilter ? t("descriptionWithResource", { topic: resourceFilter.title }) : t("description")}</p>
        </div>
      </div>
    </PageModal>
  );
};
